import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from './utils/api';
// Phase 7 sign-off: profiles short_text, long_text, identifier.
import { MAX_LENGTHS } from './utils/inputValidation';
import './ManualPaymentManager.css';

const TABS = [
  { key: 'payments', label: 'Payments' },
  { key: 'memberships', label: 'Memberships' },
  { key: 'refunds', label: 'Refunds' },
  { key: 'refund_requests', label: 'Refund Requests' },
  { key: 'reminders', label: 'Reminders' },
  { key: 'stats', label: 'Stats' },
];

const STATUS_COLORS = {
  active: '#16a34a',
  expired: '#6b7280',
  cancelled: '#dc2626',
  suspended: '#d97706',
  completed: '#16a34a',
  pending: '#d97706',
  failed: '#dc2626',
  refunded: '#6b7280',
  partially_refunded: '#d97706',
  // R4.1 — refund-request statuses. Without these all five fell through to
  // StatusBadge's grey default, so `open`, `approved` and `denied` were the
  // same pixel colour in a list an operator scans to decide what needs doing.
  open: '#d97706',        // needs attention
  in_review: '#d97706',
  approved: '#2563eb',    // distinct from 'completed' green: approved is NOT paid
  denied: '#dc2626',
  closed: '#6b7280',      // terminal; the Released column says which kind
};

function StatusBadge({ status }) {
  return (
    <span
      className="pm-badge"
      style={{ background: STATUS_COLORS[status] || '#6b7280' }}
    >
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function formatKRW(amount) {
  if (amount == null) return '—';
  return `₩${Number(amount).toLocaleString()}`;
}

function formatDate(d) {
  if (!d) return '—';
  return d.slice(0, 10);
}

function daysRemainingLabel(days) {
  if (days == null) return '—';
  if (days < 0) return <span style={{ color: '#dc2626' }}>Overdue ({Math.abs(days)}d)</span>;
  if (days === 0) return <span style={{ color: '#d97706' }}>Expires today</span>;
  return <span style={{ color: days <= 7 ? '#d97706' : '#16a34a' }}>{days}d left</span>;
}

// ── Payments Tab ─────────────────────────────────────────────────────────────

function PaymentsTab({ tiers, onRecordPayment }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ user_id: '', status: '', payment_method: '', date_from: '', date_to: '' });
  const [modal, setModal] = useState(null); // { mode: 'refund'|'edit', item }
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page, per_page: 20 });
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const res = await apiFetch(`/api/v1/admin/payments?${params}`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch { setError('Failed to load payments'); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  // R1.2 — does this payment already have a request someone is working?
  //
  // Refunding by hand here is legitimate for a payment with no request behind
  // it. Doing it for one that HAS a request is what produced the 2026-09-12
  // split-brain: the money moved, the request stayed 'open', and the term was
  // never shortened to the date the customer accepted.
  //
  // `live_only` means not closed, not denied, not already released — exactly the
  // states in which a hand-refund would strand something.
  const [liveRequests, setLiveRequests] = useState([]);

  useEffect(() => {
    if (modal?.mode !== 'refund' || !modal.item?.id) { setLiveRequests([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(
          `/api/v1/admin/refund-requests?payment_id=${encodeURIComponent(modal.item.id)}&live_only=true`);
        const j = await res.json();
        if (!cancelled) setLiveRequests(Array.isArray(j?.items) ? j.items : []);
      } catch {
        // A failed lookup must not block a legitimate refund. The banner is a
        // warning, not a gate — the real fix is passing refund_request_id below.
        if (!cancelled) setLiveRequests([]);
      }
    })();
    return () => { cancelled = true; };
  }, [modal]);

  const handleRefund = async (form) => {
    const res = await apiFetch(`/api/v1/admin/payments/${form.payment_id}/refunds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // R1.4 — name the request when there is exactly one. The server then
      // shortens the term to the date the customer accepted and closes the
      // request, i.e. behaves like /release. With more than one live request
      // the operator has to choose, so we send nothing and the banner tells
      // them to use the Refund Requests tab.
      body: JSON.stringify(
        liveRequests.length === 1
          ? { ...form, refund_request_id: liveRequests[0].id }
          : form,
      ),
    });
    if (res.ok) {
      setMessage(liveRequests.length === 1
        ? 'Refund recorded, and the customer\u2019s request was closed.'
        : 'Refund recorded successfully');
      setModal(null);
      load();
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Failed to record refund');
    }
  };

  // ── Wire confirm / reject (Track A, A8) ───────────────────────────────────
  // Replaces confirming a deposit by editing the membership's status dropdown.
  // That old route left manual_payments 'pending' forever, never provisioned the
  // org tier and never granted month-1 credits. These two calls are idempotent
  // server-side, so a double-click is safe — the API answers {status:'already'}.
  const handleConfirm = async (p) => {
    if (!window.confirm(
      `Confirm the ${formatKRW(p.amount_krw)} deposit from ${p.user_email} has arrived?\n\n` +
      `This activates the membership, sets the organisation's plan, grants the first ` +
      `month's credits and emails the customer.`
    )) return;
    setError(''); setMessage('');
    const res = await apiFetch(`/api/v1/admin/payments/${p.id}/confirm`, { method: 'POST' });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      setMessage(data.status === 'already'
        ? 'That payment was already confirmed — nothing changed.'
        : 'Payment confirmed and membership activated.');
      load();
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Failed to confirm payment');
    }
  };

  const handleReject = async (p) => {
    const reason = window.prompt(
      `Reject the pending request from ${p.user_email}?\n\n` +
      `This cancels the pending membership so they can purchase again. Reason (optional):`
    );
    if (reason === null) return;   // cancelled the prompt
    setError(''); setMessage('');
    const res = await apiFetch(`/api/v1/admin/payments/${p.id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || null }),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      setMessage(data.status === 'already'
        ? 'That request was already closed — nothing changed.'
        : 'Request rejected and the organisation released.');
      load();
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Failed to reject request');
    }
  };

  return (
    <div>
      <div className="pm-toolbar">
        <input placeholder="Filter by user ID..." value={filters.user_id}
          onChange={e => setFilters(f => ({ ...f, user_id: e.target.value }))} />
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All Statuses</option>
          {['completed','pending','failed','refunded','partially_refunded'].map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <select value={filters.payment_method} onChange={e => setFilters(f => ({ ...f, payment_method: e.target.value }))}>
          <option value="">All Methods</option>
          {['bank_transfer','cash','card','other'].map(m => (
            <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <input type="date" value={filters.date_from}
          onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        <input type="date" value={filters.date_to}
          onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
        <button className="pm-btn pm-btn-primary" onClick={() => onRecordPayment()}>+ Record Payment</button>
      </div>
      {error && <div className="pm-error">{error}<button onClick={() => setError('')}>×</button></div>}
      {message && <div className="pm-msg">{message}<button onClick={() => setMessage('')}>×</button></div>}
      <table className="pm-table">
        <thead>
          <tr>
            <th>Date</th><th>User</th><th>Tier</th><th>Amount</th>
            <th>Method</th><th>Confirmation Code</th><th>Status</th>
            <th>Refunded</th><th>Recorded By</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={10} className="pm-empty">No payments found</td></tr>}
          {items.map(p => (
            <tr key={p.id}>
              <td>{formatDate(p.payment_date)}</td>
              <td><div className="pm-user-cell"><span>{p.user_name}</span><small>{p.user_email}</small></div></td>
              <td>{p.tier_name || '—'}</td>
              <td>{formatKRW(p.amount_krw)}</td>
              <td>{p.payment_method?.replace(/_/g, ' ') || '—'}</td>
              <td><code className="pm-code">{p.confirmation_code || '—'}</code></td>
              <td><StatusBadge status={p.status} /></td>
              <td style={{ color: p.refund_total > 0 ? '#dc2626' : '#6b7280' }}>{p.refund_total > 0 ? formatKRW(p.refund_total) : '—'}</td>
              <td>{p.recorded_by_name || '—'}</td>
              <td>
                {p.status === 'pending' ? (
                  <>
                    <button className="pm-btn-link" onClick={() => handleConfirm(p)}>Confirm</button>
                    <button className="pm-btn-link pm-btn-link-danger" onClick={() => handleReject(p)}>Reject</button>
                  </>
                ) : (
                  <button className="pm-btn-link" onClick={() => setModal({ mode: 'refund', item: p })}>Refund</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pm-pagination">
        <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
        <span>Page {page} — {total} total</span>
        <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)}>Next</button>
      </div>
      {modal?.mode === 'refund' && (
        <>
          {/* R1.2 — say what this path does NOT do, before they use it. */}
          {liveRequests.length > 0 && (
            <div className="pm-overlay" style={{ zIndex: 1001, pointerEvents: 'none' }}>
              <div style={{
                pointerEvents: 'auto', maxWidth: 520, marginTop: -260,
                background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 10,
                padding: 14, fontSize: 13, color: '#92400e',
              }}>
                <strong>
                  This payment has {liveRequests.length === 1
                    ? 'an open refund request'
                    : `${liveRequests.length} open refund requests`}.
                </strong>
                {liveRequests.length === 1 ? (
                  <div style={{ marginTop: 6 }}>
                    Refunding here will also close request{' '}
                    <code>{String(liveRequests[0].id).slice(0, 8)}</code>{' '}
                    ({liveRequests[0].status}) and shorten the term to the date the
                    customer accepted. To release the exact amount they were quoted,
                    use the <strong>Refund Requests</strong> tab instead.
                  </div>
                ) : (
                  <div style={{ marginTop: 6 }}>
                    More than one is open, so this refund will <strong>not</strong> close
                    any of them and will <strong>not</strong> shorten the customer's term.
                    Resolve them on the <strong>Refund Requests</strong> tab first.
                  </div>
                )}
              </div>
            </div>
          )}
          <RefundModal payment={modal.item} onClose={() => setModal(null)} onSubmit={handleRefund} />
        </>
      )}
    </div>
  );
}

// ── Memberships Tab ──────────────────────────────────────────────────────────

function MembershipsTab({ tiers }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: '', tier_id: '' });
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', item? }
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page, per_page: 20 });
    if (filters.status) params.append('status', filters.status);
    if (filters.tier_id) params.append('tier_id', filters.tier_id);
    try {
      const res = await apiFetch(`/api/v1/admin/payments/memberships?${params}`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch { setError('Failed to load memberships'); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    const isEdit = !!form.id;
    const url = isEdit ? `/api/v1/admin/payments/memberships/${form.id}` : '/api/v1/admin/payments/memberships';
    const res = await apiFetch(url, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setMessage(isEdit ? 'Membership updated' : 'Membership assigned');
      setModal(null);
      load();
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Operation failed');
    }
  };

  return (
    <div>
      <div className="pm-toolbar">
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All Statuses</option>
          {['pending','active','expired','cancelled','suspended'].map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select value={filters.tier_id} onChange={e => setFilters(f => ({ ...f, tier_id: e.target.value }))}>
          <option value="">All Tiers</option>
          {tiers.map(t => <option key={t.id} value={t.id}>{t.display_name}</option>)}
        </select>
        <button className="pm-btn pm-btn-primary" onClick={() => setModal({ mode: 'create' })}>
          + Assign Membership
        </button>
      </div>
      {error && <div className="pm-error">{error}<button onClick={() => setError('')}>×</button></div>}
      {message && <div className="pm-msg">{message}<button onClick={() => setMessage('')}>×</button></div>}
      <table className="pm-table">
        <thead>
          <tr>
            <th>User</th><th>Tier</th><th>Category</th><th>Status</th>
            <th>Start Date</th><th>End Date</th><th>Days Left</th><th>Assigned By</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={9} className="pm-empty">No memberships found</td></tr>}
          {items.map(m => (
            <tr key={m.id}>
              <td><div className="pm-user-cell"><span>{m.user_name}</span><small>{m.user_email}</small></div></td>
              <td>{m.tier_display_name}</td>
              <td>
                <span className={`pm-category-chip ${m.tier_category === 'fgi_partner' ? 'pm-fgi' : 'pm-standard'}`}>
                  {m.tier_category === 'fgi_partner' ? 'FGI Partner' : 'Standard'}
                </span>
              </td>
              <td><StatusBadge status={m.status} /></td>
              <td>{formatDate(m.start_date)}</td>
              <td>{formatDate(m.end_date)}</td>
              <td>{daysRemainingLabel(m.days_remaining)}</td>
              <td>{m.assigned_by_name || '—'}</td>
              <td>
                <button className="pm-btn-link" onClick={() => setModal({ mode: 'edit', item: m })}>Edit</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pm-pagination">
        <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
        <span>Page {page} — {total} total</span>
        <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)}>Next</button>
      </div>
      {modal && (
        <MembershipModal
          mode={modal.mode}
          item={modal.item}
          tiers={tiers}
          onClose={() => setModal(null)}
          onSubmit={handleSave}
        />
      )}
    </div>
  );
}

// ── Refunds Tab ──────────────────────────────────────────────────────────────

function RefundsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page, per_page: 20 });
    try {
      const res = await apiFetch(`/api/v1/admin/payments/reminders/history?${params}`);
      // Use refunds from all payments via a different approach — query all payments and collect refunds
      // Fetch all refunds by querying without payment_id filter
    } catch { /* ignore */ }
    // Direct approach: list all payments then compile refunds isn't ideal;
    // we rely on the payments list showing refund_total.
    // For this tab we show refunds from a dedicated endpoint if available,
    // otherwise we note they are viewable per-payment.
    setItems([]);
    setTotal(0);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  // Actual refunds list — fetch from summary endpoint
  const [refunds, setRefunds] = useState([]);
  useEffect(() => {
    (async () => {
      // We aggregate by fetching payments with status refunded/partially_refunded
      const res = await apiFetch('/api/v1/admin/payments?status=refunded&per_page=100');
      const res2 = await apiFetch('/api/v1/admin/payments?status=partially_refunded&per_page=100');
      const d1 = res.ok ? await res.json() : { items: [] };
      const d2 = res2.ok ? await res2.json() : { items: [] };
      setRefunds([...d1.items, ...d2.items]);
    })();
  }, []);

  return (
    <div>
      <p className="pm-tab-desc">Showing all payments with refunds issued. Refund details are recorded per payment.</p>
      <table className="pm-table">
        <thead>
          <tr>
            <th>Payment Date</th><th>User</th><th>Original Amount</th>
            <th>Refunded Total</th><th>Method</th><th>Status</th>
          </tr>
        </thead>
        <tbody>
          {refunds.length === 0 && <tr><td colSpan={6} className="pm-empty">No refunded payments found</td></tr>}
          {refunds.map(p => (
            <tr key={p.id}>
              <td>{formatDate(p.payment_date)}</td>
              <td><div className="pm-user-cell"><span>{p.user_name}</span><small>{p.user_email}</small></div></td>
              <td>{formatKRW(p.amount_krw)}</td>
              <td style={{ color: '#dc2626' }}>{formatKRW(p.refund_total)}</td>
              <td>{p.payment_method?.replace(/_/g, ' ') || '—'}</td>
              <td><StatusBadge status={p.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Reminders Tab ────────────────────────────────────────────────────────────

function RemindersTab() {
  const [dueUsers, setDueUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [reminderType, setReminderType] = useState('payment_due');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const loadDue = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/payments/reminders/due');
      if (res.ok) setDueUsers(await res.json());
    } catch { /* ignore */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/payments/reminders/history?per_page=50');
      if (res.ok) {
        const data = await res.json();
        setHistory(data.items || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadDue(); loadHistory(); }, [loadDue, loadHistory]);

  const toggleUser = (uid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === dueUsers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(dueUsers.map(u => u.user_id)));
    }
  };

  const handleSend = async () => {
    setShowConfirm(false);
    setSending(true);
    setResult(null);
    setError('');
    try {
      const res = await apiFetch('/api/v1/admin/payments/reminders/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_ids: [...selected],
          reminder_type: reminderType,
          custom_message: customMessage || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setSelected(new Set());
        loadHistory();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Failed to send reminders');
      }
    } catch { setError('Network error'); }
    finally { setSending(false); }
  };

  return (
    <div>
      {error && <div className="pm-error">{error}<button onClick={() => setError('')}>×</button></div>}
      {result && (
        <div className="pm-msg">
          Sent {result.total_sent} reminder(s). {result.total_failed > 0 && `${result.total_failed} failed.`}
          <button onClick={() => setResult(null)}>×</button>
        </div>
      )}

      <div className="pm-reminders-grid">
        {/* Left: Users due */}
        <div className="pm-reminders-panel">
          <h3>Users with Payment Due <span className="pm-count-chip">{dueUsers.length}</span></h3>
          <div className="pm-toolbar">
            <label className="pm-checkbox-label">
              <input type="checkbox"
                checked={dueUsers.length > 0 && selected.size === dueUsers.length}
                onChange={toggleAll}
              />
              Select All
            </label>
          </div>
          <table className="pm-table">
            <thead>
              <tr><th></th><th>User</th><th>Tier</th><th>Expiry</th><th>Status</th></tr>
            </thead>
            <tbody>
              {dueUsers.length === 0 && <tr><td colSpan={5} className="pm-empty">No users with payment due</td></tr>}
              {dueUsers.map(u => (
                <tr key={u.user_id} className={selected.has(u.user_id) ? 'pm-row-selected' : ''}>
                  <td>
                    <input type="checkbox" checked={selected.has(u.user_id)}
                      onChange={() => toggleUser(u.user_id)} />
                  </td>
                  <td><div className="pm-user-cell"><span>{u.full_name}</span><small>{u.email}</small></div></td>
                  <td>{u.tier_display_name}</td>
                  <td>{formatDate(u.end_date)}</td>
                  <td>
                    <span className={`pm-badge ${u.reminder_status === 'overdue' ? '' : ''}`}
                      style={{ background: u.reminder_status === 'overdue' ? '#dc2626' : '#d97706' }}>
                      {u.reminder_status === 'overdue' ? 'Overdue' : 'Expiring Soon'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Right: Compose */}
        <div className="pm-reminders-compose">
          <h3>Compose Reminder</h3>
          <div className="pm-form-group">
            <label>Reminder Type</label>
            <select value={reminderType} onChange={e => setReminderType(e.target.value)}>
              <option value="payment_due">Payment Due</option>
              <option value="membership_expiring">Membership Expiring</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div className="pm-form-group">
            <label>Custom Message (optional)</label>
            <textarea
              rows={4}
              maxLength={MAX_LENGTHS.long_text}
              placeholder="Add a personalized message to include in the reminder..."
              value={customMessage}
              onChange={e => setCustomMessage(e.target.value)}
            />
          </div>
          <p className="pm-selected-count">
            {selected.size} user{selected.size !== 1 ? 's' : ''} selected
          </p>
          <button
            className="pm-btn pm-btn-primary pm-btn-full"
            disabled={selected.size === 0 || sending}
            onClick={() => setShowConfirm(true)}
          >
            {sending ? 'Sending...' : 'Send Reminders'}
          </button>
        </div>
      </div>

      {/* Sending History */}
      <div className="pm-reminders-history">
        <h3>Reminder History</h3>
        <table className="pm-table">
          <thead>
            <tr><th>Sent At</th><th>User</th><th>Type</th><th>Sent By</th><th>Status</th></tr>
          </thead>
          <tbody>
            {history.length === 0 && <tr><td colSpan={5} className="pm-empty">No reminders sent yet</td></tr>}
            {history.map(r => (
              <tr key={r.id}>
                <td>{formatDate(r.sent_at)}</td>
                <td>{r.user_name || r.user_email || '—'}</td>
                <td>{r.reminder_type?.replace(/_/g, ' ')}</td>
                <td>{r.sent_by_name || '—'}</td>
                <td><StatusBadge status={r.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="pm-overlay" onClick={() => setShowConfirm(false)}>
          <div className="pm-modal" onClick={e => e.stopPropagation()}>
            <h3>Confirm Send</h3>
            <p>Send <strong>{reminderType.replace(/_/g, ' ')}</strong> reminders to <strong>{selected.size}</strong> user(s)?</p>
            <div className="pm-modal-actions">
              <button className="pm-btn pm-btn-secondary" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="pm-btn pm-btn-primary" onClick={handleSend}>Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stats Tab ────────────────────────────────────────────────────────────────

function StatsTab() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await apiFetch('/api/v1/admin/payments/stats');
      if (res.ok) setStats(await res.json());
    })();
  }, []);

  if (!stats) return <div className="pm-loading">Loading stats...</div>;

  return (
    <div>
      <div className="pm-stats-grid">
        <div className="pm-stat-card">
          <span className="pm-stat-value">{stats.active_memberships ?? '—'}</span>
          <span className="pm-stat-label">Active Memberships</span>
        </div>
        <div className="pm-stat-card pm-stat-warning">
          <span className="pm-stat-value">{stats.expiring_soon ?? '—'}</span>
          <span className="pm-stat-label">Expiring Soon (7d)</span>
        </div>
        <div className="pm-stat-card pm-stat-danger">
          <span className="pm-stat-value">{stats.overdue_count ?? '—'}</span>
          <span className="pm-stat-label">Overdue</span>
        </div>
        <div className="pm-stat-card pm-stat-success">
          <span className="pm-stat-value">{formatKRW(stats.total_revenue_krw)}</span>
          <span className="pm-stat-label">Total Revenue</span>
        </div>
        <div className="pm-stat-card pm-stat-danger">
          <span className="pm-stat-value">{formatKRW(stats.total_refunds_krw)}</span>
          <span className="pm-stat-label">Total Refunds</span>
        </div>
        <div className="pm-stat-card">
          <span className="pm-stat-value">{stats.total_payments ?? '—'}</span>
          <span className="pm-stat-label">Total Payments</span>
        </div>
      </div>

      {stats.tier_breakdown?.length > 0 && (
        <div className="pm-tier-breakdown">
          <h3>Active Members by Tier</h3>
          <table className="pm-table">
            <thead><tr><th>Tier</th><th>Active Members</th></tr></thead>
            <tbody>
              {stats.tier_breakdown.map(t => (
                <tr key={t.tier}>
                  <td>{t.tier}</td>
                  <td>{t.member_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Modals ───────────────────────────────────────────────────────────────────

function RecordPaymentModal({ tiers, onClose, onSubmit }) {
  const [memberships, setMemberships] = useState([]);
  const [form, setForm] = useState({
    user_id: '', membership_id: '', amount_krw: '',
    payment_method: 'bank_transfer', confirmation_code: '',
    payment_date: new Date().toISOString().slice(0, 10),
    description: '', status: 'completed',
  });
  const [error, setError] = useState('');

  // Typeahead state for cross-org user search.
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!userQuery || userQuery.trim().length < 2 || selectedUser) {
      setUserResults([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiFetch(`/api/v1/admin/payments/user-lookup?q=${encodeURIComponent(userQuery.trim())}&limit=25`);
        if (res.ok) {
          const data = await res.json();
          setUserResults(data.items || []);
          setShowResults(true);
        } else {
          setUserResults([]);
        }
      } catch {
        setUserResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [userQuery, selectedUser]);

  useEffect(() => {
    if (form.user_id) {
      apiFetch(`/api/v1/admin/payments/users/${form.user_id}/memberships`)
        .then(r => r.ok && r.json()).then(d => setMemberships(Array.isArray(d) ? d : []));
    } else {
      setMemberships([]);
    }
  }, [form.user_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickUser = (u) => {
    setSelectedUser(u);
    set('user_id', u.id);
    setUserQuery('');
    setUserResults([]);
    setShowResults(false);
  };

  const clearUser = () => {
    setSelectedUser(null);
    set('user_id', '');
    set('membership_id', '');
    setUserQuery('');
    setUserResults([]);
  };

  const handleSubmit = async () => {
    if (!form.user_id || !form.amount_krw || !form.payment_date) {
      setError('User, amount, and payment date are required');
      return;
    }
    if (Number(form.amount_krw) <= 0) { setError('Amount must be greater than 0'); return; }
    onSubmit({ ...form, amount_krw: Number(form.amount_krw), membership_id: form.membership_id || null });
  };

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-modal pm-modal-wide" onClick={e => e.stopPropagation()}>
        <h3>Record Manual Payment</h3>
        {error && <div className="pm-modal-error">{error}</div>}
        <div className="pm-form-grid">
          <div className="pm-form-group pm-span-2" style={{ position: 'relative' }}>
            <label>User *</label>
            {selectedUser ? (
              <div className="pm-user-chip" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f3f4f6', borderRadius: 6, fontSize: 13 }}>
                <span>
                  <strong>{selectedUser.full_name || '(no name)'}</strong>{' '}
                  &lt;{selectedUser.email}&gt;
                  {selectedUser.org_name ? ` · ${selectedUser.org_name}` : ''}
                </span>
                <button type="button" onClick={clearUser} aria-label="Clear selected user"
                  style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#6b7280' }}>×</button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Search by name or email (min 2 chars)..."
                  value={userQuery}
                  onChange={e => setUserQuery(e.target.value)}
                  onFocus={() => userResults.length > 0 && setShowResults(true)}
                  onBlur={() => setTimeout(() => setShowResults(false), 150)}
                  autoComplete="off"
                />
                {showResults && userResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                    maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  }}>
                    {userResults.map(u => (
                      <div
                        key={u.id}
                        onMouseDown={() => pickUser(u)}
                        style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >
                        <div><strong>{u.full_name || '(no name)'}</strong> &lt;{u.email}&gt;</div>
                        <div style={{ color: '#6b7280', fontSize: 11 }}>
                          {u.org_name || 'No org'} · {u.role}{!u.is_active ? ' · inactive' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {searching && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Searching…</div>}
                {userQuery.trim().length >= 2 && !searching && userResults.length === 0 && (
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>No matches</div>
                )}
              </>
            )}
          </div>
          <div className="pm-form-group pm-span-2">
            <label>Membership</label>
            <select value={form.membership_id} onChange={e => set('membership_id', e.target.value)}>
              <option value="">No linked membership</option>
              {memberships.map(m => (
                <option key={m.id} value={m.id}>{m.tier_display_name} ({formatDate(m.start_date)} – {formatDate(m.end_date)})</option>
              ))}
            </select>
          </div>
          <div className="pm-form-group">
            <label>Amount (₩) *</label>
            <input type="number" min="1" value={form.amount_krw} onChange={e => set('amount_krw', e.target.value)} />
          </div>
          <div className="pm-form-group">
            <label>Payment Date *</label>
            <input type="date" value={form.payment_date} onChange={e => set('payment_date', e.target.value)} />
          </div>
          <div className="pm-form-group">
            <label>Payment Method</label>
            <select value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
              {['bank_transfer','cash','card','other'].map(m => (
                <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div className="pm-form-group">
            <label>Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)}>
              {['completed','pending','failed'].map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="pm-form-group pm-span-2">
            <label>Confirmation Code</label>
            <input type="text" placeholder="e.g. TXN-20260402-ABCD"
              maxLength={MAX_LENGTHS.identifier}
              value={form.confirmation_code} onChange={e => set('confirmation_code', e.target.value)} />
          </div>
          <div className="pm-form-group pm-span-2">
            <label>Description</label>
            <textarea rows={2} maxLength={MAX_LENGTHS.long_text} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
        </div>
        <div className="pm-modal-actions">
          <button className="pm-btn pm-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="pm-btn pm-btn-primary" onClick={handleSubmit}>Record Payment</button>
        </div>
      </div>
    </div>
  );
}

function MembershipModal({ mode, item, tiers, onClose, onSubmit }) {
  const [form, setForm] = useState(
    mode === 'edit' && item
      ? { id: item.id, user_id: item.user_id, membership_tier_id: item.membership_tier_id,
          start_date: formatDate(item.start_date), end_date: formatDate(item.end_date),
          status: item.status, notes: item.notes || '' }
      : { user_id: '', membership_tier_id: '', start_date: '', end_date: '', notes: '' }
  );
  const [error, setError] = useState('');

  // Cross-org user typeahead (create mode only).
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (mode !== 'create') return undefined;
    if (!userQuery || userQuery.trim().length < 2 || selectedUser) {
      setUserResults([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiFetch(`/api/v1/admin/payments/user-lookup?q=${encodeURIComponent(userQuery.trim())}&limit=25`);
        if (res.ok) {
          const data = await res.json();
          setUserResults(data.items || []);
          setShowResults(true);
        } else {
          setUserResults([]);
        }
      } catch {
        setUserResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [userQuery, selectedUser, mode]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickUser = (u) => {
    setSelectedUser(u);
    set('user_id', u.id);
    setUserQuery('');
    setUserResults([]);
    setShowResults(false);
  };

  const clearUser = () => {
    setSelectedUser(null);
    set('user_id', '');
    setUserQuery('');
    setUserResults([]);
  };

  const handleQuickSet = (years) => {
    if (!form.start_date) return;
    const start = new Date(form.start_date);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + years);
    set('end_date', end.toISOString().slice(0, 10));
  };

  const handleSubmit = () => {
    if (!form.start_date || !form.end_date) { setError('Start and end date are required'); return; }
    if (new Date(form.end_date) < new Date(form.start_date)) { setError('End date must be on or after start date'); return; }
    if (mode === 'create' && !form.user_id) { setError('User is required'); return; }
    if (!form.membership_tier_id) { setError('Membership tier is required'); return; }
    onSubmit(form);
  };

  const standardTiers = tiers.filter(t => t.category === 'standard');
  const fgiTiers = tiers.filter(t => t.category === 'fgi_partner');

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-modal pm-modal-wide" onClick={e => e.stopPropagation()}>
        <h3>{mode === 'edit' ? 'Edit Membership' : 'Assign Membership'}</h3>
        {error && <div className="pm-modal-error">{error}</div>}
        <div className="pm-form-grid">
          {mode === 'create' && (
            <div className="pm-form-group pm-span-2" style={{ position: 'relative' }}>
              <label>User *</label>
              {selectedUser ? (
                <div className="pm-user-chip" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#f3f4f6', borderRadius: 6, fontSize: 13 }}>
                  <span>
                    <strong>{selectedUser.full_name || '(no name)'}</strong>{' '}
                    &lt;{selectedUser.email}&gt;
                    {selectedUser.org_name ? ` · ${selectedUser.org_name}` : ''}
                  </span>
                  <button type="button" onClick={clearUser} aria-label="Clear selected user"
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#6b7280' }}>×</button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Search by name or email (min 2 chars)..."
                    value={userQuery}
                    onChange={e => setUserQuery(e.target.value)}
                    onFocus={() => userResults.length > 0 && setShowResults(true)}
                    onBlur={() => setTimeout(() => setShowResults(false), 150)}
                    autoComplete="off"
                  />
                  {showResults && userResults.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                      maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}>
                      {userResults.map(u => (
                        <div
                          key={u.id}
                          onMouseDown={() => pickUser(u)}
                          style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                          <div><strong>{u.full_name || '(no name)'}</strong> &lt;{u.email}&gt;</div>
                          <div style={{ color: '#6b7280', fontSize: 11 }}>
                            {u.org_name || 'No org'} · {u.role}{!u.is_active ? ' · inactive' : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {searching && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Searching…</div>}
                  {userQuery.trim().length >= 2 && !searching && userResults.length === 0 && (
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>No matches</div>
                  )}
                </>
              )}
            </div>
          )}
          <div className="pm-form-group pm-span-2">
            <label>Membership Tier *</label>
            <select value={form.membership_tier_id} onChange={e => set('membership_tier_id', e.target.value)}>
              <option value="">Select tier...</option>
              {standardTiers.length > 0 && (
                <optgroup label="Standard">
                  {standardTiers.map(t => <option key={t.id} value={t.id}>{t.display_name}</option>)}
                </optgroup>
              )}
              {fgiTiers.length > 0 && (
                <optgroup label="FGI Partner">
                  {fgiTiers.map(t => <option key={t.id} value={t.id}>{t.display_name}</option>)}
                </optgroup>
              )}
            </select>
          </div>
          <div className="pm-form-group">
            <label>Start Date *</label>
            <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
          </div>
          <div className="pm-form-group">
            <label>End Date *</label>
            <input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
          </div>
          <div className="pm-form-group pm-span-2">
            <label>Quick Set Duration</label>
            <div className="pm-quick-set">
              <button
                type="button"
                className="pm-btn pm-btn-secondary pm-quick-btn"
                disabled={!form.start_date}
                onClick={() => handleQuickSet(1)}
                title={!form.start_date ? 'Set a start date first' : 'Set end date to 1 year from start date'}
              >
                + 1 Year
              </button>
              <button
                type="button"
                className="pm-btn pm-btn-secondary pm-quick-btn"
                disabled={!form.start_date}
                onClick={() => handleQuickSet(2)}
                title={!form.start_date ? 'Set a start date first' : 'Set end date to 2 years from start date'}
              >
                + 2 Years
              </button>
              {form.start_date && form.end_date && (
                <span className="pm-duration-preview">
                  → {formatDate(form.end_date)}
                </span>
              )}
            </div>
          </div>
          {mode === 'edit' && (
            <div className="pm-form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                {['pending','active','expired','cancelled','suspended'].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          )}
          <div className={`pm-form-group ${mode === 'edit' ? '' : 'pm-span-2'}`}>
            <label>Notes</label>
            <textarea rows={2} maxLength={MAX_LENGTHS.long_text} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        {mode === 'create' && (
          <div className="pm-modal-warning">
            ⚠ This will expire any current active membership for this user.
          </div>
        )}
        <div className="pm-modal-actions">
          <button className="pm-btn pm-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="pm-btn pm-btn-primary" onClick={handleSubmit}>
            {mode === 'edit' ? 'Update Membership' : 'Assign Membership'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RefundModal({ payment, onClose, onSubmit, initialAmount }) {
  const existingRefunds = payment.refund_total || 0;
  const remaining = payment.amount_krw - existingRefunds;
  const [form, setForm] = useState({
    payment_id: payment.id,
    // N4.3 — prefill with the pro-rata quote when opened from a refund request.
    refund_amount_krw: initialAmount != null ? String(initialAmount) : '',
    reason: '',
    refund_method: 'bank_transfer',
    refund_confirmation_code: '',
    refund_date: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (!form.reason) { setError('Reason is required'); return; }
    if (!form.refund_amount_krw || Number(form.refund_amount_krw) <= 0) { setError('Refund amount must be greater than 0'); return; }
    if (Number(form.refund_amount_krw) > remaining) { setError(`Refund amount cannot exceed remaining refundable amount ${formatKRW(remaining)}`); return; }
    onSubmit({ ...form, refund_amount_krw: Number(form.refund_amount_krw) });
  };

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={e => e.stopPropagation()}>
        <h3>Issue Refund</h3>
        <div className="pm-refund-info">
          <div><span>Payment:</span> <code>{payment.id?.slice(0, 8).toUpperCase()}</code></div>
          <div><span>User:</span> {payment.user_name}</div>
          <div><span>Original Amount:</span> {formatKRW(payment.amount_krw)}</div>
          <div><span>Already Refunded:</span> {formatKRW(existingRefunds)}</div>
          <div><span>Remaining:</span> <strong style={{ color: '#16a34a' }}>{formatKRW(remaining)}</strong></div>
        </div>
        {error && <div className="pm-modal-error">{error}</div>}
        <div className="pm-form-grid">
          <div className="pm-form-group">
            <label>Refund Amount (₩) *</label>
            <input type="number" min="1" max={remaining} value={form.refund_amount_krw}
              onChange={e => set('refund_amount_krw', e.target.value)} />
          </div>
          <div className="pm-form-group">
            <label>Refund Date *</label>
            <input type="date" value={form.refund_date} onChange={e => set('refund_date', e.target.value)} />
          </div>
          <div className="pm-form-group pm-span-2">
            <label>Reason *</label>
            <input type="text" maxLength={MAX_LENGTHS.short_text} value={form.reason} onChange={e => set('reason', e.target.value)} />
          </div>
          <div className="pm-form-group">
            <label>Refund Method</label>
            <select value={form.refund_method} onChange={e => set('refund_method', e.target.value)}>
              {['bank_transfer','credit','other'].map(m => (
                <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div className="pm-form-group">
            <label>Confirmation Code</label>
            <input type="text" value={form.refund_confirmation_code}
              maxLength={MAX_LENGTHS.identifier}
              onChange={e => set('refund_confirmation_code', e.target.value)} />
          </div>
        </div>
        <div className="pm-modal-actions">
          <button className="pm-btn pm-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="pm-btn pm-btn-danger" onClick={handleSubmit}>Issue Refund</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ManualPaymentManager() {
  const [activeTab, setActiveTab] = useState('payments');
  const [tiers, setTiers] = useState([]);
  const [paymentModal, setPaymentModal] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    apiFetch('/api/v1/admin/payments/tiers')
      .then(r => r.ok && r.json())
      .then(d => setTiers(Array.isArray(d) ? d : []));
  }, []);

  const handleRecordPayment = async (form) => {
    const res = await apiFetch('/api/v1/admin/payments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setMessage('Payment recorded successfully');
      setPaymentModal(false);
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Failed to record payment');
    }
  };

  return (
    <div className="pm-page">
      <div className="pm-header">
        <h1>Manual Payment Manager</h1>
        <button className="pm-btn pm-btn-primary" onClick={() => setPaymentModal(true)}>
          + Record Payment
        </button>
      </div>

      {error && <div className="pm-error">{error}<button onClick={() => setError('')}>×</button></div>}
      {message && <div className="pm-msg">{message}<button onClick={() => setMessage('')}>×</button></div>}

      <div className="pm-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`pm-tab ${activeTab === t.key ? 'pm-tab-active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="pm-content">
        {activeTab === 'payments' && (
          <PaymentsTab tiers={tiers} onRecordPayment={() => setPaymentModal(true)} />
        )}
        {activeTab === 'memberships' && <MembershipsTab tiers={tiers} />}
        {activeTab === 'refunds' && <RefundsTab />}
        {activeTab === 'refund_requests' && <RefundRequestsTab />}
        {activeTab === 'reminders' && <RemindersTab />}
        {activeTab === 'stats' && <StatsTab />}
      </div>

      {paymentModal && (
        <RecordPaymentModal
          tiers={tiers}
          onClose={() => setPaymentModal(false)}
          onSubmit={handleRecordPayment}
        />
      )}
    </div>
  );
}

// -- Refund Requests Tab ------------------------------------------------------
// User-submitted refund inquiries (VeriPass Refund Policy 2026-05-28).
// These are intake records — the money is released from the dialog below,
// or by hand on the Payments tab for a payment with no request behind it.
/* The server's transition table, mirrored so the UI cannot offer a move the
   server will refuse. refund_request_service.admin_update is the AUTHORITY --
   this is a convenience, not a check, and it must stay in step with it.

   Why it matters: the dropdown used to list all five statuses whatever the
   current one was, so an operator could pick `approved` on a `closed` request
   and get "Invalid transition: closed -> approved" after clicking Save. That
   refusal is correct, but offering the option at all is the UI wasting an
   operator's time on a decision the server had already made. */
const ALLOWED_TRANSITIONS = {
  open: ['in_review', 'approved', 'denied', 'closed'],
  in_review: ['approved', 'denied', 'closed'],
  approved: ['closed'],
  denied: ['closed'],
  closed: [],
};

function RefundRequestsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null); // { item, status, notes }
  const [quote, setQuote] = useState(null);        // N4.2/N4.3 pro-rata quote for the open request
  // T4.2 — releasing the customer's accepted quote. Since R1.3 this is the
  // ONLY money-moving action in this tab.
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState('');
  // R1.3 — `refundFor` and its RefundModal were deleted with the
  // "Execute refund…" button. The free-text path lives on the Payments tab
  // (see the RefundModal at the top of this file), which is the only place
  // it belongs: a dialog about a QUOTED request should not offer an
  // un-quoted refund alongside it.
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page, per_page: 25 });
    if (statusFilter) params.append('status', statusFilter);
    try {
      const res = await apiFetch(`/api/v1/admin/refund-requests?${params}`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
        setError('');
      } else {
        const j = await res.json().catch(() => ({}));
        setError(j.detail || `Failed to load refund requests (HTTP ${res.status})`);
      }
    } catch (e) {
      setError(e?.message || 'Network error loading refund requests');
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // N4.3 — fetch the read-only pro-rata quote whenever a request is opened.
  useEffect(() => {
    const id = editing?.item?.id;
    if (!id) { setQuote(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/v1/admin/refund-requests/${id}/quote`);
        const data = await res.json().catch(() => null);
        if (!cancelled) setQuote(res.ok ? data : null);
      } catch { if (!cancelled) setQuote(null); }
    })();
  return () => { cancelled = true; };
  }, [editing?.item?.id]);

  // ⚠ T4.2 — RELEASE the customer's accepted quote. Distinct from
  // handleExecuteRefund above: that one moves a hand-set amount through the
  // free-text modal and is tied to nothing the customer agreed to. This pays
  // the snapshot stored on the request and shortens their term.
  // R3.5 — the last point at which a wrong row is recoverable.
  const [confirmText, setConfirmText] = useState('');
  const [released, setReleased] = useState(null);   // the terminal success panel

  const releaseRefund = useCallback(async () => {
    if (!editing) return;
    setReleaseError('');
    setReleasing(true);
    try {
      const res = await apiFetch(
        `/api/v1/admin/refund-requests/${editing.item.id}/release`,
        { method: 'POST' },
      );
      // Captured before any state churn: the terminal panel below reports what
      // actually moved, and an operator mid-incident should not have to go
      // hunting the list to find out whether the click worked.
      const releasedAmount = editing.item.quoted_amount_krw;
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        // R3.5 — do NOT close on success. The dialog switches to a terminal
        // panel naming the amount. Closing instantly leaves the operator asking
        // "did that work?" about a money action, and the only way to answer was
        // to find the row again in a list where released and denied requests
        // both read `closed`.
        setReleased({ amount: releasedAmount });
        setConfirmText('');
        load();
      } else {
        // Surfaced verbatim. On a transport-unknown failure the message tells
        // the operator NOT to retry, which is the one instruction that matters.
        setReleaseError(d.detail || 'Release failed.');
      }
    } catch (e) {
      setReleaseError('Network error while releasing the refund.');
    }
    setReleasing(false);
  }, [editing, load]);

  // R1.3 — `handleExecuteRefund` lived here. It POSTed a hand-set amount to
  // /admin/payments/{id}/refunds, the route that does not close the request and
  // does not shorten the term. It existed only to feed the "Execute refund…"
  // modal, which is gone, so it is gone too. Removing the function as well as
  // the button matters: a dead handler is an invitation to wire a new button to
  // it. The free-text path remains on the Payments tab, where the operator is
  // not looking at a quoted request while they use it.

  // R4.4 — CLOSING A MONEY DIALOG IS A DECISION, NOT A REFLEX.
  //
  // R0.4 removed the outside-click dismissal that was silently discarding an
  // operator's typed notes. Escape has to arrive at the same answer: offer it,
  // because keyboard users need a way out, but confirm when there is work to
  // lose. "Did I already type that?" is not a question to leave someone holding
  // while a customer waits on a refund.
  //
  // `openerRef` restores focus to the row's Update button on close. Without it
  // a keyboard user lands back at the top of the document and has to tab
  // through the whole table to reach the row they were just on.
  const openerRef = useRef(null);
  const dialogRef = useRef(null);

  const requestClose = useCallback(() => {
    // Once the money has moved there is nothing left to lose, and `editing.item`
    // is stale against the row `load()` just refetched — so the dirty check
    // would prompt about "unsaved changes" that no longer mean anything.
    const dirty = !released && editing
      && (editing.status !== editing.item.status
          || (editing.notes || '') !== (editing.item.resolution_notes || ''));
    if (dirty && !window.confirm('Discard your unsaved changes to this refund request?')) {
      return;
    }
    setEditing(null);
    setReleased(null);
    setConfirmText('');
    if (openerRef.current) {
      try { openerRef.current.focus(); } catch { /* element may be gone after a reload */ }
    }
  }, [editing, released]);

  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); requestClose(); }
    };
    document.addEventListener('keydown', onKey);
    // Move focus INTO the dialog. Otherwise a screen reader stays on the table
    // behind it and reads nothing that just appeared.
    const t = setTimeout(() => {
      try { dialogRef.current?.focus(); } catch { /* not mounted yet */ }
    }, 0);
    return () => { document.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [editing, requestClose]);

  const submitUpdate = async () => {
    if (!editing) return;
    try {
      const res = await apiFetch(`/api/v1/admin/refund-requests/${editing.item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: editing.status, resolution_notes: editing.notes || null }),
      });
      if (res.ok) {
        setMessage('Refund request updated');
        setEditing(null);
        load();
      } else {
        const j = await res.json().catch(() => ({}));
        setError(j.detail || 'Failed to update');
      }
    } catch (e) {
      setError('Failed to update');
    }
  };

  return (
    <div>
      {error && <div className="pm-error">{error}</div>}
      {message && <div className="pm-success">{message}</div>}

      <div className="pm-filters">
        <select value={statusFilter} onChange={e => { setPage(1); setStatusFilter(e.target.value); }}>
          <option value="">All statuses</option>
          {['open', 'in_review', 'approved', 'denied', 'closed'].map(s => (
            <option key={s} value={s}>{s.replace('_', ' ')}</option>
          ))}
        </select>
      </div>

      <table className="pm-table">
        <thead>
          <tr>
            <th>Submitted</th><th>User</th><th>Payment</th><th>Reason</th>
            {/* R4.1 — Released is its own column because `closed` is written by
                TWO different things: a real release, and an admin closing a
                denied request. Without it, "we paid this customer" and "we told
                them no" are the same word in the same colour. */}
            <th>Status</th><th>Released</th><th>Resolved</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map(rr => (
            <tr key={rr.id}>
              <td>{formatDate(rr.submitted_at)}</td>
              <td>{rr.user_name || rr.user_email || rr.user_id?.slice(0, 8)}</td>
              <td>{rr.payment_id ? rr.payment_id.slice(0, 8) : '—'}</td>
              <td style={{ maxWidth: 280, fontSize: 12 }}>{(rr.reason || '').slice(0, 140)}{rr.reason?.length > 140 ? '…' : ''}</td>
              <td><StatusBadge status={rr.status} /></td>
              {/* The money question, answerable without opening the row. */}
              <td>
                {rr.released_refund_id
                  ? <span style={{ color: '#16a34a', fontWeight: 600 }}>yes</span>
                  : (rr.status === 'closed' || rr.status === 'denied')
                    ? <span style={{ color: '#6b7280' }}>no</span>
                    : <span style={{ color: '#6b7280' }}>—</span>}
              </td>
              <td>{rr.resolved_at ? formatDate(rr.resolved_at) : '—'}</td>
              <td>
                <button
                  className="pm-btn-link"
                  onClick={(e) => {
                    // Remember the opener so focus can return here on close.
                    openerRef.current = e.currentTarget;
                    // R3.5 — never carry a typed confirmation between rows.
                    setConfirmText('');
                    setReleased(null);
                    setReleaseError('');
                    setEditing({ item: rr, status: rr.status, notes: rr.resolution_notes || '' });
                  }}
                >
                  Update
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={7} style={{ textAlign: 'center', color: '#6b7280', padding: 16 }}>No refund requests</td></tr>
          )}
        </tbody>
      </table>

      <div className="pm-pagination">
        <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
        <span>Page {page} of {Math.max(1, Math.ceil(total / 25))}</span>
        <button disabled={page >= Math.ceil(total / 25)} onClick={() => setPage(page + 1)}>Next</button>
      </div>

      {/* R0.4 — TWO BUGS ON THIS ONE LINE.
            (1) The class was `pm-modal-overlay`. Only `pm-overlay` exists in
                ManualPaymentManager.css:343, and every other modal in this file
                uses it (:580, :739, :929, :1095). So this dialog alone rendered
                with NO overlay styling: not fixed, not dimmed, not centred — it
                sat inline in the page flow behind the table.
            (2) The overlay dismissed on outside click. On a dialog that RELEASES
                MONEY, a stray click discarded the operator's typed notes and
                status with no confirmation. Closing is now explicit only:
                the Cancel button at :1507. */}
      {editing && (
        <div className="pm-overlay">
          <div
            className="pm-modal"
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rr-dialog-title"
            tabIndex={-1}
          >
            <h3 id="rr-dialog-title">Update refund request</h3>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
              From {editing.item.user_email || editing.item.user_name}
            </div>
            <div style={{ fontSize: 13, marginBottom: 12, padding: 8, background: '#f9fafb', borderRadius: 6 }}>
              {editing.item.reason}
            </div>

            {/* N4.3 — pro-rata quote + one-click execution into the existing RefundModal */}
            {quote && quote.quotable && (
              <div style={{ fontSize: 13, marginBottom: 12, padding: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
                {/* R3.3 — `quote.basis` used to sit here. It was generated BEFORE
                    the clamp, so the screen could read "미사용 11개월 × ₩19,900 =
                    ₩218,900" directly above "Pro-rata refundable: ₩0". Three
                    numbers, no statement of which was authoritative. The server
                    no longer sends a prose summary that can contradict its own
                    total; these fields are the reasoning. */}
                <div><strong>Refundable now:</strong> <strong style={{ color: '#16a34a' }}>{formatKRW(quote.refundable_krw)}</strong></div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  {quote.months_refunded} of {quote.total_units} month(s) · {formatKRW(quote.unit_price_krw)}/month
                  {quote.new_end_date ? ` · term would end ${quote.new_end_date}` : ''}
                </div>
                {quote.clamped_by_prior_refunds && (
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 2 }}>
                    Capped by what remains after earlier refunds.
                  </div>
                )}
                {/* Q5 varies per customer, so the reason travels with the number. */}
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Current month {quote.current_month_refunded ? 'included' : 'excluded'}
                  {quote.current_month_determinable === false ? ' (usage unreadable — withheld)' : ''}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Paid {formatKRW(quote.amount_paid_krw)} · already refunded {formatKRW(quote.already_refunded_krw)}
                </div>
                {/* R3.1 — the accepted snapshot beside the live quote, labelled.
                    They diverge legitimately: time passes, other refunds land.
                    Release pays the SNAPSHOT. Showing only one number invites
                    the operator to assume it is the one that will be paid. */}
                {quote.snapshot_amount_krw != null
                  && quote.snapshot_amount_krw !== quote.refundable_krw && (
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 4 }}>
                    Customer accepted <strong>{formatKRW(quote.snapshot_amount_krw)}</strong>
                    {quote.snapshot_months ? ` for ${quote.snapshot_months} month(s)` : ''}
                    . Release pays that snapshot, not the live figure above.
                  </div>
                )}
                {/* R1.3 — "Execute refund…" WAS A BUTTON HERE. It is gone.

                    It opened a free-text RefundModal prefilled from the LIVE
                    quote and posted to POST /admin/payments/{id}/refunds — a
                    route that knows nothing about refund_requests. It did not
                    close the request and it passed no new_end_date, so the term
                    fell back to grace-to-period-end instead of the date the
                    customer accepted.

                    On 2026-09-12 an operator used it, reasonably, because it sat
                    directly above Release and read like the action. The customer
                    was refunded ₩238,800 in full, the request stayed `open`, and
                    they kept an extra month of paid access. The comment below
                    this block used to argue "keep both"; that argument is what
                    the incident cost.

                    The escape hatch still exists twice — the Payments tab and
                    RefundManager — so a third copy inside the dialog whose whole
                    purpose is releasing a QUOTED amount bought nothing. */}
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                  Need a different amount? Refund by hand on the <strong>Payments</strong> tab.
                  That will <strong>not</strong> close this request and <strong>not</strong> shorten
                  the customer's term — record what you did in the notes below.
                </div>
              </div>
            )}
            {/* ⚠ T4.2 / R1.3 — RELEASE IS THE ONLY ACTION IN THIS DIALOG.

                It pays the SNAPSHOT the customer accepted when they filed the
                request, shortens their term to the quoted end date, and is gated
                server-side on the request being `approved`. It is idempotent: the
                database allows one release per request.

                This block used to end "Keep both" — arguing that the free-text
                "Execute refund…" button above was a useful escape hatch. It was
                removed on 2026-09-13. Two money buttons side by side, one of
                which silently fails to close the request or shorten the term, is
                not a choice an operator can be expected to get right under
                pressure; on 2026-09-12 one did not.
                See REFUND-PROCESS-ERROR-9-12.md §0.2 and §3.2. */}
            {/* R3.5 — TERMINAL SUCCESS. Money moved; there is nothing further to
                do on this row, so the dialog says so rather than dropping the
                operator back into a form that still looks actionable. */}
            {released && (
              <div style={{ fontSize: 13, marginBottom: 12, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6 }}>
                <div style={{ fontWeight: 700, color: '#15803d' }}>
                  Refund released — {formatKRW(released.amount)}
                </div>
                <div style={{ fontSize: 12, color: '#166534', marginTop: 4 }}>
                  The request is closed and the customer's term has been shortened.
                  Do not release again; the database allows one release per request.
                </div>
              </div>
            )}

            {/* R3.5 — NULL QUOTE. A request with no stored quote can NEVER be
                released: release() reads quoted_amount_krw. Showing a form that
                looks actionable invites an operator to try, fail, and go looking
                for the free-text route — which is exactly how 2026-09-12
                happened. Say plainly that there is nothing to release. */}
            {!released && !editing.item.released_refund_id
              && !(editing.item.quoted_amount_krw > 0) && (
              <div style={{ fontSize: 13, marginBottom: 12, padding: 10, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, color: '#6b7280' }}>
                <strong>No quote stored on this request.</strong> It cannot be released
                — there is no accepted amount to pay. Close it with a note explaining
                why. If the customer is still owed a refund, ask them to file again
                from Settings › Payments so a fresh quote is recorded.
              </div>
            )}

            {!released && editing.item.quoted_amount_krw > 0 && !editing.item.released_refund_id && (
              <div style={{ fontSize: 13, marginBottom: 12, padding: 10, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6 }}>
                <div>
                  <strong>Customer requested:</strong>{' '}
                  <strong style={{ color: '#1d4ed8' }}>{formatKRW(editing.item.quoted_amount_krw)}</strong>
                  {editing.item.requested_months ? ` for ${editing.item.requested_months} month(s)` : ''}
                </div>
                {editing.item.quoted_new_end_date && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    Term will be shortened to {editing.item.quoted_new_end_date}
                  </div>
                )}

                {/* ⚠ R3.5 — GATE ON THE PERSISTED STATUS, NOT THE DROPDOWN.
                    This read `editing.status`, which is the UNSAVED local
                    selection. Picking "approved" in the dropdown enabled Release
                    immediately, while the hint below still said to save first.
                    The server refuses correctly, but only after the operator has
                    committed to a money action and been told "no" for a reason
                    that is not obviously their own fault. */}
                {editing.item.status !== 'approved' ? (
                  <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>
                    This request is <strong>{editing.item.status}</strong>. Set the status to{' '}
                    <strong>approved</strong> and <strong>Save</strong> before releasing.
                    {editing.status === 'approved' && editing.status !== editing.item.status && (
                      <> Your change to <strong>approved</strong> is not saved yet.</>
                    )}
                  </div>
                ) : (
                  <>
                    {/* The retype. Release is irreversible from this screen and
                        the rows look alike — same customer, same date, same
                        reason. Typing the amount is the cheapest way to make the
                        operator read the number they are about to send. */}
                    <label style={{ display: 'block', fontSize: 12, color: '#374151', marginTop: 8 }}>
                      Type the amount to confirm:{' '}
                      <strong>{editing.item.quoted_amount_krw.toLocaleString()}</strong>
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={String(editing.item.quoted_amount_krw)}
                        style={{ display: 'block', marginTop: 4, padding: '6px 8px', width: 180 }}
                      />
                    </label>
                    <button
                      className="pm-btn-link"
                      style={{ marginTop: 8 }}
                      disabled={
                        releasing
                        || confirmText.replace(/[^0-9]/g, '') !== String(editing.item.quoted_amount_krw)
                      }
                      onClick={releaseRefund}
                    >
                      {releasing ? 'Releasing…' : 'Release this refund'}
                    </button>
                  </>
                )}
                {releaseError && (
                  <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>{releaseError}</div>
                )}
              </div>
            )}

            {editing.item.released_refund_id && (
              <div style={{ fontSize: 12, color: '#16a34a', marginBottom: 12 }}>
                Released{editing.item.released_at ? ` on ${new Date(editing.item.released_at).toLocaleString()}` : ''}.
              </div>
            )}

            {quote && !quote.quotable && (
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                {quote.reason || 'No automatic quote available for this request.'}
              </div>
            )}

            <label>
              Status
              <select
                value={editing.status}
                disabled={(ALLOWED_TRANSITIONS[editing.item.status] || []).length === 0}
                onChange={e => setEditing({ ...editing, status: e.target.value })}
              >
                {/* The CURRENT status, plus only the moves the server permits
                    from it. A same-status save is a legal no-op, so the current
                    value always stays selectable. */}
                {[editing.item.status, ...(ALLOWED_TRANSITIONS[editing.item.status] || [])]
                  .filter((v, i, arr) => arr.indexOf(v) === i)
                  .map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))}
              </select>
              {(ALLOWED_TRANSITIONS[editing.item.status] || []).length === 0 && (
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  This request is <strong>{editing.item.status}</strong>, which is final.
                  {editing.item.released_refund_id
                    ? ' The refund was released and the request is settled.'
                    : ' No refund was released against it.'}
                </div>
              )}
            </label>
            <label>
              Resolution notes
              <textarea
                value={editing.notes}
                onChange={e => setEditing({ ...editing, notes: e.target.value })}
                rows={4}
                maxLength={2000}
              />
            </label>
            <div className="pm-modal-actions">
              <button className="pm-btn-secondary" onClick={requestClose}>Cancel</button>
              <button className="pm-btn-primary" onClick={submitUpdate}>Save</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
